import { View } from "@pimo/pimo-app-builder";
import {
  DragAndDropLayout,
  PMBarChartCard,
  PMDonutChartCard,
} from "@pimo/pimo-components";
import { APP_RECORD_TYPES, Configuration } from "vmd-types";

import { TestAppState } from "../../../app";
import { DONUT_CARD_LAYOUT, DONUT_CHART_COLORS } from "../../../constants";
import { filterReportingItemsByRecordType } from "../../../utils/filter-reporting-items-by-record-type";
import {
  ADOIT_ID_FIELD_NAME,
  findFormQuestionIdByFieldName,
  getRecordSource,
} from "../../report-plugin";
import { withTotalInTitle } from "../with-total-in-title";

// Fixed order/colors so the bar and donut chart line up with each other
// (#2599). "User input" is intentionally excluded per the ticket's scoping.
const RECORD_SOURCE_CATEGORIES = ["ADOIT", "Archer", "ADOIT & Archer"] as const;

const getRecordSourceData = (
  items: ReturnType<typeof filterReportingItemsByRecordType>,
  configuration: Configuration | undefined
) => {
  const adoitIdQuestionId = findFormQuestionIdByFieldName(
    configuration,
    ADOIT_ID_FIELD_NAME
  );

  const counts = new Map<string, number>(
    RECORD_SOURCE_CATEGORIES.map((category) => [category, 0])
  );

  for (const item of items) {
    const recordSource = getRecordSource(item, adoitIdQuestionId);
    if (recordSource === "User input") continue;
    counts.set(recordSource, (counts.get(recordSource) ?? 0) + 1);
  }

  const entries = RECORD_SOURCE_CATEGORIES.map((label, index) => ({
    label,
    count: counts.get(label) ?? 0,
    color: DONUT_CHART_COLORS[index % DONUT_CHART_COLORS.length],
  }));

  return { entries };
};

/** Adds the record-source bar chart and donut chart (#2599). */
export function addRecordSourceCards(
  view: View<TestAppState, DragAndDropLayout>
) {
  const recordSourceBarChartComponent = view.addComponent({
    component: PMBarChartCard,
    layoutProps: {
      componentId: "recordSourceBarChartComponent",
      labels: ["barChart", "chart", "recordSource"],
      description: "Shows Business Apps grouped by record source.",
      title: "Business Apps per Record Source (Bar Chart)",
    },
  });

  recordSourceBarChartComponent.mapState(({ currentReport, settings }) => {
    const items = filterReportingItemsByRecordType(
      currentReport?.project?.reportingItems,
      APP_RECORD_TYPES.BUSINESS_APPLICATION
    );
    const { entries } = getRecordSourceData(items, settings?.configuration);

    return {
      title: "Business Apps per Record Source (Bar Chart)",
      categories: entries.map((entry) => entry.label),
      data: entries.map((entry) => entry.count),
      chartType: 1 as const,
      xLabel: "Business Apps",
      yLabel: "Record Source",
      colors: entries.map((entry) => entry.color),
      fixedHeight: "320px",
    };
  });

  const recordSourceDonutChartComponent = view.addComponent({
    component: PMDonutChartCard,
    layoutProps: {
      componentId: "recordSourceDonutChartComponent",
      labels: ["donutChart", "chart", "recordSource"],
      description: "Shows Business Apps grouped by record source.",
      title: "Business Apps per Record Source",
    },
  });

  recordSourceDonutChartComponent.mapState(({ currentReport, settings }) => {
    const items = filterReportingItemsByRecordType(
      currentReport?.project?.reportingItems,
      APP_RECORD_TYPES.BUSINESS_APPLICATION
    );
    const { entries } = getRecordSourceData(items, settings?.configuration);

    return {
      title: withTotalInTitle("Business Apps per Record Source", items.length),
      icon: "DonutLarge",
      ...DONUT_CARD_LAYOUT,
      data: entries.map((entry) => ({
        label: entry.label,
        value: entry.count,
        color: entry.color,
      })),
    };
  });
}
